import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {lazy, Suspense, useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import WebhookLayoutPlugin from './plugins/WebhookLayoutPlugin';
import SwaggerUiPageHeader from './SwaggerUiPageHeader';

const SwaggerUI = lazy(() => import(/* webpackChunkName: "swagger-ui" */ 'swagger-ui-react'));

const SPEC_FILE_PATH = `/webhooks/openapi.yaml`;

export default function WebhookReferencePage(): JSX.Element {
  useEffect(() => {
    const hideNoOperationsDefined = (observer: MutationObserver): void => {
      document.querySelectorAll('.block.col-12 h3').forEach((elem) => {
        if (elem.textContent?.includes('No operations defined in spec!')) {
          elem.classList.add('hidden');
          observer.disconnect(); // disconnect the observer after adding the hidden class
        }
      });
    };
    // Use observer because SwaggerUI renders internal components asynchronously
    const observer = new MutationObserver(() => {
      hideNoOperationsDefined(observer);
    });

    const listenerFunc = (): void => {
      // See https://github.com/swagger-api/swagger-ui/issues/1369#issuecomment-885356639 - substitute to deep linking for models
      const elemId = window.location.hash.replace('!/', '').replace('/', '').replace('#', '');
      setTimeout(() => {
        const elem = document.getElementById(`model-${elemId}`);
        if (elem) {
          const anchor = elem.querySelector('.model');
          if (anchor) {
            (anchor as HTMLElement).click();
          }
          setTimeout(() => {
            const top = elem.offsetTop;
            window.scrollTo({top, behavior: 'auto'});
          }, 100);
        }
      }, 700); // If deep linking starts failing, increase this timeout to give Swagger UI more time to render

      const htmlElement = document.querySelector('html');
      if (htmlElement) {
        observer.observe(htmlElement, {childList: true, subtree: true});
        hideNoOperationsDefined(observer); // Initial check
      }
    };

    setWindowTitle('Benchling Webhooks Documentation');
    window.addEventListener('load', listenerFunc);
    return () => {
      window.removeEventListener('load', listenerFunc);
    };
  }, []);

  return (
    <div>
      <SwaggerUiPageHeader />
      <div className={classNames(css(styles.contentWrapper))}>
        <p className={classNames(css(styles.introduction))}>
          This page provides reference documentation for the schemas of supported webhook message types.
        </p>
        <Suspense fallback={<CenteredHorizontalThrobber />}>
          <SwaggerUI url={SPEC_FILE_PATH} plugins={[WebhookLayoutPlugin]} docExpansion="list" />
        </Suspense>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  contentWrapper: {
    margin: '40px auto',
    width: '90%',
    maxWidth: '1300px',
  },
  introduction: {
    fontSize: '15px',
  },
});
